'use client';

import { flip, offset, shift } from '@floating-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// --- Hooks ---
import { useFloatingElement } from '@/hooks/use-floating-element';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import { getLinkBoundingRect, getLinkRange } from './get-link-rect';
import { LinkMain } from './link-popover';
import { useLinkPopover } from './use-link-popover';

const FLOATING_Z_INDEX = 1000;

export interface LinkBubbleProps {
    editor?: import('@tiptap/react').Editor | null;
}

export function LinkBubble({ editor: providedEditor }: LinkBubbleProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const [linkRect, setLinkRect] = useState<DOMRect | null>(null);
    const [linkRange, setLinkRange] = useState<{ from: number; to: number } | null>(null);
    const bubbleRef = useRef<HTMLDivElement | null>(null);

    const { isActive, url, setUrl, setLink, removeLink, openLink } = useLinkPopover({
        editor,
    });

    const updateLinkRect = useCallback(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }
        if (editor.isActive('link')) {
            const rect = getLinkBoundingRect(editor);
            const range = getLinkRange(editor);
            setLinkRect(rect);
            setLinkRange(range);
        } else {
            if (bubbleRef.current?.contains(document.activeElement)) {
                return;
            }
            setLinkRect(null);
            setLinkRange(null);
        }
    }, [editor]);

    const handleSetLink = useCallback(() => {
        if (!editor || editor.isDestroyed) return;
        if (linkRange) {
            editor.chain().focus().setTextSelection(linkRange).run();
        }
        setLink();
        const pos = editor.state.selection.to;
        editor.chain().setTextSelection(pos).run();
        setLinkRect(null);
        setLinkRange(null);
    }, [editor, linkRange, setLink]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            setLinkRect(null); // eslint-disable-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-external-store-subscription -- Disabling since this is TipTap ui code
            setLinkRange(null); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription -- Disabling since this is TipTap ui code
            return;
        }

        updateLinkRect();
        editor.on('selectionUpdate', updateLinkRect);

        const handleScroll = () => {
            if (editor.isActive('link')) {
                setLinkRect(getLinkBoundingRect(editor));
            }
        };

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            editor.off('selectionUpdate', updateLinkRect);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [editor, updateLinkRect]);

    const show = isActive && linkRect !== null;

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show, linkRect, FLOATING_Z_INDEX, {
        placement: 'bottom-start',
        middleware: [
            offset(8),
            flip({
                mainAxis: true,
                crossAxis: false,
            }),
            shift(),
        ],
    });

    const setBubbleRef = useCallback(
        (node: HTMLDivElement | null) => {
            bubbleRef.current = node;
            ref(node);
        },
        [ref],
    );

    if (!isMounted || !show || !linkRect) {
        return null;
    }

    const bubbleContent = (
        <div
            ref={setBubbleRef}
            style={{ ...style, pointerEvents: 'auto' }}
            {...getFloatingProps()}
            className="tiptap-link-bubble"
            role="dialog"
            aria-label="Edit link"
        >
            <LinkMain
                url={url}
                setUrl={setUrl}
                setLink={handleSetLink}
                removeLink={removeLink}
                openLink={openLink}
                isActive={isActive}
                autoFocusInput={false}
            />
        </div>
    );

    return createPortal(bubbleContent, document.body);
}
